import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AtSign, BadgeCheck, CircleAlert, Loader2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, prefix, value, onChange, status, hint, testId, placeholder }) => (
  <div>
    <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{label}</label>
    <div className={`flex items-center bg-[#0B0C0E] border rounded-lg px-3 transition-colors ${status?.error ? 'border-red-500/60' : status?.ok ? 'border-emerald-500/60' : 'border-[#2A2F3D] focus-within:border-amber-500'}`}>
      {prefix && <span className="text-slate-500 font-mono text-sm mr-1">{prefix}</span>}
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="flex-1 bg-transparent outline-none py-2.5 text-white text-sm" />
      {status?.checking && <Loader2 size={14} className="text-slate-500 animate-spin" />}
      {!status?.checking && status?.ok && <BadgeCheck size={15} className="text-emerald-500" />}
      {!status?.checking && status?.error && <CircleAlert size={15} className="text-red-500" />}
    </div>
    <p data-testid={`${testId}-msg`} className={`text-[11px] mt-1.5 leading-snug ${status?.error ? 'text-red-400' : status?.ok ? 'text-emerald-400' : 'text-slate-500'}`}>
      {status?.error || (status?.ok ? 'Disponible' : hint)}
    </p>
  </div>
);

export const IdentityEditor = ({ profile, onSaved }) => {
  const [handle, setHandle] = useState(profile.handle || '');
  const [nick, setNick] = useState(profile.nick || profile.name || '');
  const [status, setStatus] = useState({ handle: {}, nick: {} });
  const [saving, setSaving] = useState(false);
  const timer = useRef();

  useEffect(() => {
    const changedHandle = handle !== (profile.handle || '');
    const changedNick = nick !== (profile.nick || '');
    if (!changedHandle && !changedNick) { setStatus({ handle: {}, nick: {} }); return; }
    setStatus((s) => ({ handle: changedHandle ? { checking: true } : {}, nick: changedNick ? { checking: true } : {} }));
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const params = new URLSearchParams();
      if (changedHandle && handle) params.set('handle', handle);
      if (changedNick && nick) params.set('nick', nick);
      if (![...params.keys()].length) { setStatus({ handle: {}, nick: {} }); return; }
      try {
        const res = await fetch(`${API}/account/check?${params}`, { credentials: 'include' });
        const data = await res.json();
        setStatus({
          handle: changedHandle && handle ? (data.errors.handle ? { error: data.errors.handle } : { ok: true }) : {},
          nick: changedNick && nick ? (data.errors.nick ? { error: data.errors.nick } : { ok: true }) : {},
        });
      } catch { setStatus({ handle: {}, nick: {} }); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [handle, nick, profile.handle, profile.nick]);

  const dirty = handle !== (profile.handle || '') || nick !== (profile.nick || '');
  const blocked = status.handle.error || status.nick.error || status.handle.checking || status.nick.checking;

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (handle !== (profile.handle || '')) body.handle = handle;
      if (nick !== (profile.nick || '')) body.nick = nick;
      const res = await fetch(`${API}/account/identity`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        const errs = data?.detail?.errors || {};
        setStatus({ handle: errs.handle ? { error: errs.handle } : {}, nick: errs.nick ? { error: errs.nick } : {} });
        toast.error('Revisa los campos marcados en rojo');
        return;
      }
      toast.success('ID y nick actualizados');
      onSaved?.(data);
    } catch {
      toast.error('No se pudo guardar. Inténtalo de nuevo.');
    } finally { setSaving(false); }
  };

  return (
    <div className="sc-card p-6 md:p-8 mb-6" data-testid="identity-card">
      <div className="flex items-center gap-2 mb-2">
        <AtSign size={14} className="text-amber-500" />
        <h2 className="sc-mono-eyebrow text-amber-500">Identidad en Stage Crew</h2>
      </div>
      <p className="text-sm text-slate-400 mb-6 leading-relaxed">
        Tu <span className="text-slate-200">ID</span> es único y sirve para que te encuentren (@id). Tu <span className="text-slate-200">nick</span> es el nombre que ven los demás. Ninguno puede repetirse entre miembros ni contener términos ofensivos, sexuales u obscenos.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="ID de usuario" prefix="@" value={handle} onChange={(v) => setHandle(v.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20))} status={status.handle} hint="3-20 caracteres · minúsculas, números y _" testId="identity-handle-input" placeholder="laura_luces" />
        <Field label="Nick visible" value={nick} onChange={(v) => setNick(v.slice(0, 30))} status={status.nick} hint="2-30 caracteres · es el nombre que verán los demás" testId="identity-nick-input" placeholder="Laura Luces" />
      </div>
      <div className="flex justify-end mt-5">
        <button data-testid="identity-save-btn" onClick={save} disabled={!dirty || !!blocked || saving} className="inline-flex items-center gap-2 border border-amber-500/50 text-amber-500 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm">
          <Save size={14} /> {saving ? 'Guardando…' : 'Guardar ID y nick'}
        </button>
      </div>
    </div>
  );
};

export default IdentityEditor;
